import { useCallback, useMemo } from 'react';
import { create } from 'zustand';

export interface StepStore {
  step: number;
  value: unknown;
  transition: boolean;
  setStep: (step: number, value?: unknown) => void;
  resetStep: () => void;
  nextStep: (value?: unknown) => void;
  prevStep: () => void;
  setTransition: (transition: boolean) => void;
}

export const useStepStore = create<StepStore>((set) => ({
  step: 2,
  value: '',
  transition: false,
  setStep: (step: number, value?: unknown) => {
    set({
      step: step,
      value,
    });
  },
  resetStep: () => {
    set({
      step: 2,
      value: '',
    });
  },
  nextStep: (value = '') => {
    set((state) => ({
      step: state.step + 1,
      value,
    }));
  },
  prevStep: (value = '') => {
    set((state) => ({
      step: Math.max(state.step - 1, 1),
      value,
    }));
  },
  setTransition: (transition) => {
    set({
      transition,
    });
  },
}));

export const useStepValue = <T>(defaultValue: unknown = ''): T => {
  const { value } = useStepStore();
  return (value ?? defaultValue) as T;
};

const actionRef: { current?: () => void } = {};

export const useStepTransition = () => {
  const { nextStep, prevStep, resetStep, setTransition, transition } = useStepStore();

  const startTransition = useCallback(
    (action?: () => void) => {
      setTransition(true);
      actionRef.current = action;
    },
    [setTransition]
  );

  const endTransition = useCallback(
    (fn?: () => void) => {
      setTransition(false);
      fn?.();
      actionRef.current?.();
    },
    [setTransition]
  );

  const startPrevTransition = useCallback(
    (fn?: () => void) => {
      startTransition(() => {
        prevStep();
        fn?.();
      });
    },
    [prevStep, startTransition]
  );

  const startNextTransition = useCallback(
    (fn?: () => void, value?: unknown) => {
      startTransition(() => {
        nextStep(value);
        fn?.();
      });
    },
    [startTransition, nextStep]
  );

  const resetTransition = useCallback(() => {
    setTransition(false);
    resetStep();
    actionRef.current = undefined;
  }, [resetStep, setTransition]);

  return useMemo(
    () => ({ transition, startTransition, endTransition, resetTransition, startPrevTransition, startNextTransition }),
    [startNextTransition, resetTransition, startPrevTransition, transition, startTransition, endTransition]
  );
};
